// HTML → Block[] parser for "Magic Paste" when clipboard provides
// rich HTML (Notion, Google Docs, generic web pages).
//
// We walk top-level children of the pasted fragment and map each
// recognised element to one of our block types. Whatever falls outside
// the whitelist is unwrapped to its text content and pushed as a
// paragraph (best-effort, never throw).

import {
  type AudioEmbedBlock,
  type Block,
  type DividerBlock,
  type EmbedProvider,
  type HeadingBlock,
  type ImageBlock,
  type LinkCardBlock,
  type ListBlock,
  type ParagraphBlock,
  type QuoteBlock,
  type VideoEmbedBlock,
  detectProvider,
  makeBlock,
  sanitizeInlineHtml,
} from "./blocks";

const VIDEO_PROVIDERS: EmbedProvider[] = [
  "youtube",
  "vimeo",
  "instagram",
  "tiktok",
];
const AUDIO_PROVIDERS: EmbedProvider[] = [
  "soundcloud",
  "spotify",
  "bandcamp",
  "apple_music",
  "artlist",
];

export function htmlToBlocks(html: string): Block[] {
  if (!html) return [];

  // Wrap into a host so DOMParser preserves fragments outside <body>.
  const doc = new DOMParser().parseFromString(
    `<!doctype html><html><body><div id="__paste__">${html}</div></body></html>`,
    "text/html",
  );
  const host = doc.getElementById("__paste__");
  if (!host) return [];

  // Notion pastes lots of meta wrappers (<meta>, <style>, comment nodes).
  // Strip them out before walking.
  for (const sel of ["meta", "style", "script", "link"]) {
    host.querySelectorAll(sel).forEach((n) => n.remove());
  }

  // If we have a single wrapper child that itself contains the real flow
  // (Google Docs wraps in <b style="font-weight:normal">), descend into it.
  let root: Element = host;
  while (
    root.children.length === 1 &&
    isLayoutWrapper(root.children[0] as Element)
  ) {
    root = root.children[0] as Element;
  }

  const out: Block[] = [];
  for (const child of Array.from(root.children)) {
    walk(child as Element, out);
  }

  // Final pass — merge consecutive paragraphs that came from inline
  // wrappers? Not needed, leave as-is.
  return out;
}

function isLayoutWrapper(el: Element): boolean {
  const tag = el.tagName.toLowerCase();
  return tag === "div" || tag === "span" || tag === "b" || tag === "section";
}

function walk(el: Element, out: Block[]): void {
  const tag = el.tagName.toLowerCase();

  switch (tag) {
    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "h5":
    case "h6": {
      const level = Math.min(3, Number(tag.slice(1))) as 1 | 2 | 3;
      const text = (el.textContent || "").trim();
      if (text) out.push(heading(level, text));
      return;
    }
    case "p": {
      // Detect "p with a single standalone link or image" first.
      const single = singleMeaningfulChild(el);
      if (single) {
        const childTag = single.tagName.toLowerCase();
        if (childTag === "img") {
          out.push(imageFromImg(single as HTMLImageElement));
          return;
        }
        if (childTag === "a" && single.textContent === el.textContent) {
          const a = single as HTMLAnchorElement;
          const href = a.getAttribute("href") || "";
          if (/^https?:\/\//i.test(href)) {
            out.push(linkOrEmbed(href, (a.textContent || "").trim()));
            return;
          }
        }
      }
      const clean = sanitizeInlineHtml(el.innerHTML);
      if (clean.replace(/<[^>]*>/g, "").trim()) {
        out.push(paragraph(clean));
      }
      return;
    }
    case "blockquote": {
      const text = (el.textContent || "").trim();
      if (text) out.push(quote(text));
      return;
    }
    case "ul":
    case "ol": {
      const items: string[] = [];
      el.querySelectorAll(":scope > li").forEach((li) => {
        const txt = (li.textContent || "").trim();
        if (txt) items.push(txt);
      });
      if (items.length > 0) out.push(listBlock(tag === "ol", items));
      return;
    }
    case "hr": {
      out.push(divider());
      return;
    }
    case "img": {
      out.push(imageFromImg(el as HTMLImageElement));
      return;
    }
    case "figure": {
      // <figure><img/><figcaption/></figure>
      const img = el.querySelector("img") as HTMLImageElement | null;
      if (img) {
        const cap = el.querySelector("figcaption");
        const block = imageFromImg(img);
        if (cap) block.caption = (cap.textContent || "").trim();
        out.push(block);
        return;
      }
      // Generic figure — walk children.
      for (const c of Array.from(el.children)) walk(c as Element, out);
      return;
    }
    case "a": {
      const href = (el as HTMLAnchorElement).getAttribute("href") || "";
      if (/^https?:\/\//i.test(href)) {
        out.push(linkOrEmbed(href, (el.textContent || "").trim()));
      }
      return;
    }
    case "br":
      return;
    case "div":
    case "section":
    case "article":
    case "main":
    case "span":
    case "b": {
      // Container — descend if there are block-like children, else treat
      // as a paragraph.
      const blockChildren = Array.from(el.children).filter((c) =>
        isBlockTag(c.tagName.toLowerCase()),
      );
      if (blockChildren.length > 0) {
        for (const c of Array.from(el.children)) walk(c as Element, out);
        return;
      }
      const clean = sanitizeInlineHtml(el.innerHTML);
      if (clean.replace(/<[^>]*>/g, "").trim()) {
        out.push(paragraph(clean));
      }
      return;
    }
    default: {
      // Unknown tag — fall back to text content as paragraph.
      const text = (el.textContent || "").trim();
      if (text) {
        out.push(paragraph(escapeHtml(text)));
      }
      return;
    }
  }
}

function isBlockTag(tag: string): boolean {
  return (
    tag === "h1" ||
    tag === "h2" ||
    tag === "h3" ||
    tag === "h4" ||
    tag === "h5" ||
    tag === "h6" ||
    tag === "p" ||
    tag === "blockquote" ||
    tag === "ul" ||
    tag === "ol" ||
    tag === "hr" ||
    tag === "figure" ||
    tag === "img" ||
    tag === "div" ||
    tag === "section" ||
    tag === "article"
  );
}

function singleMeaningfulChild(el: Element): Element | null {
  let found: Element | null = null;
  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      if ((node.textContent || "").trim() === "") continue;
      return null;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      if (found) return null;
      found = node as Element;
    }
  }
  return found;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// ── Factories ─────────────────────────────────────────────────────────────

function heading(level: 1 | 2 | 3, text: string): HeadingBlock {
  const b = makeBlock("heading") as HeadingBlock;
  b.level = level;
  b.text = text;
  return b;
}

function paragraph(html: string): ParagraphBlock {
  const b = makeBlock("paragraph") as ParagraphBlock;
  b.html = html;
  return b;
}

function quote(text: string): QuoteBlock {
  const b = makeBlock("quote") as QuoteBlock;
  b.text = text;
  return b;
}

function divider(): DividerBlock {
  const b = makeBlock("divider") as DividerBlock;
  b.style = "line";
  return b;
}

function listBlock(ordered: boolean, items: string[]): ListBlock {
  const b = makeBlock("list") as ListBlock;
  b.ordered = ordered;
  b.items = items.length > 0 ? items : [""];
  return b;
}

function imageFromImg(img: HTMLImageElement): ImageBlock {
  const b = makeBlock("image") as ImageBlock;
  const src = img.getAttribute("src") || "";
  if (src) b.url = src;
  const alt = img.getAttribute("alt") || "";
  if (alt) b.alt = alt;
  return b;
}

function linkOrEmbed(url: string, title?: string): Block {
  const provider = detectProvider(url);
  if (VIDEO_PROVIDERS.includes(provider)) {
    const b = makeBlock("video_embed") as VideoEmbedBlock;
    b.url = url;
    b.provider = provider;
    return b;
  }
  if (AUDIO_PROVIDERS.includes(provider)) {
    const b = makeBlock("audio_embed") as AudioEmbedBlock;
    b.url = url;
    b.provider = provider;
    return b;
  }
  const b = makeBlock("link_card") as LinkCardBlock;
  b.url = url;
  if (title && title !== url) b.title = title;
  return b;
}
